/**
 * Favourite order quantities (owner 2026-10-04): the DOM quantity row shows the starred quantities
 * as chips, and the chart order bar's quick-quantity menu lists the same ones. The user picks them
 * from 1–10; typed quantities above 10 still work in the stepper. Stored in the synced settings
 * (`qty_favorites`), so every device shows the same chips. Untrusted: read through
 * `sanitizeQtyFavorites`. Pure; covered by qtyFavorites.test.ts.
 */

/** The quantities that can be starred, in list order. */
export const QTY_FAVORITE_CHOICES: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/** Favourites of a user who never chose any (and the reading of a missing or unusable value). */
export const DEFAULT_QTY_FAVORITES: readonly number[] = [1, 2, 3, 5, 10];

/** The one-line reason shown when the last favourite would be unstarred. */
export const LAST_QTY_FAVORITE_REASON = "Keep at least one favourite.";

/**
 * A stored value as the favourites to show: the valid choices (1–10, whole numbers), each once, in
 * ascending order. Anything else (missing in older documents, not a list, nothing valid left)
 * reads as the defaults, so at least one favourite always shows.
 */
export function sanitizeQtyFavorites(v: unknown): number[] {
  if (!Array.isArray(v)) return [...DEFAULT_QTY_FAVORITES];
  const kept = new Set<number>();
  for (const n of v) if (typeof n === "number" && QTY_FAVORITE_CHOICES.includes(n)) kept.add(n);
  return kept.size === 0 ? [...DEFAULT_QTY_FAVORITES] : [...kept].sort((a, b) => a - b);
}

export type QtyFavoriteToggle = { ok: true; favorites: number[] } | { ok: false; reason: string };

/**
 * Star or unstar `qty`. Unstarring the last favourite is refused with a reason (at least one must
 * remain); a quantity outside 1–10 leaves the list as it is.
 */
export function toggleQtyFavorite(list: unknown, qty: number): QtyFavoriteToggle {
  const cur = sanitizeQtyFavorites(list);
  if (!QTY_FAVORITE_CHOICES.includes(qty)) return { ok: true, favorites: cur };
  if (!cur.includes(qty)) return { ok: true, favorites: [...cur, qty].sort((a, b) => a - b) };
  if (cur.length === 1) return { ok: false, reason: LAST_QTY_FAVORITE_REASON };
  return { ok: true, favorites: cur.filter((n) => n !== qty) };
}
